import { Image, Text, View } from "@react-pdf/renderer";
import { useContext } from "react";
import { removeDiacritics } from "../utils";
import { BreakLine, RedBulletPointSquare, RedSlash } from "./Components";
import { horizontalPadding, perelynRed, verticalPadding } from "./const";
import { CVContentContext, LanguageContext } from "./CVPDF";
import RequirementsMatchTable from "./RequirementsMatchTable";
import { HeaderRow, Row, Table } from "./TableComponents";
import { LargeTextPDF, SmallTextPDF } from "./Texts";
import { cvTranslations } from "./translations";

export default function FirstPageContent() {
  const cvContent = useContext<any>(CVContentContext) || {};
  const language = useContext(LanguageContext);
  const t = cvTranslations[language];
  const lessThanTwoCertificates =
    !cvContent["certificates_logos"]?.length ||
    cvContent["certificates_logos"]?.length <= 1;

  const certificateLogosPresent = cvContent["certificates_logos"]?.length > 0;

  const estimateContentOverflow = () => {
    const headlineCount = (cvContent["headlines"] || []).length;
    const educationCount = (cvContent["education"] || []).length;
    const certificateTextCount = (cvContent["certificates"] || []).length;
    const hasRequirementsMatch =
      cvContent.requirementsMatch?.requirements?.length > 0;

    let contentUnits = 0;
    contentUnits += headlineCount * 1.5;
    contentUnits += educationCount * 2;
    contentUnits += certificateTextCount * 1.5;
    contentUnits += hasRequirementsMatch ? 5 : 0;

    return contentUnits > 12;
  };

  const shouldUseSmallerLogo = estimateContentOverflow();
  const logoSize = shouldUseSmallerLogo ? "90px" : "125px";
  return (
    cvContent && (
      <>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <View
            style={{
              paddingLeft: horizontalPadding,
              paddingTop: verticalPadding,
              paddingBottom: verticalPadding,
              maxWidth: "70%",
            }}
          >
            <SmallTextPDF
              style={{
                color: perelynRed,
              }}
            >
              {t.profile}
            </SmallTextPDF>
            <LargeTextPDF>{cvContent["fullName"] || ""}</LargeTextPDF>
            <Text
              style={{
                fontSize: "14px",
                fontFamily: "ABCWhyte",
              }}
            >
              {cvContent["jobTitle"] || ""}
            </Text>
            <SmallTextPDF>Perelyn, {cvContent["location"] || ""}</SmallTextPDF>
          </View>
          {cvContent["fullName"] && (
            <Image
              src={`${
                process.env.REACT_APP_PROFILE_PICTURES_BUCKET_URL
              }/${removeDiacritics(cvContent["fullName"]).replaceAll(
                " ",
                "-"
              )}.jpg`}
              style={{
                width: "150px",
                height: "150px",
              }}
            />
          )}
        </View>
        {cvContent.requirementsMatch &&
          cvContent.requirementsMatch.requirements &&
          cvContent.requirementsMatch.requirements.length > 0 && (
            <>
              <BreakLine />
              <View
                style={{ padding: `${verticalPadding} ${horizontalPadding}` }}
              >
                <RequirementsMatchTable />
              </View>
            </>
          )}
        <BreakLine />
        <View
          style={{
            padding: `${verticalPadding} ${horizontalPadding}`,
          }}
        >
          <SmallTextPDF
            style={{
              color: perelynRed,
            }}
          >
            {t.summary}
          </SmallTextPDF>
          <SmallTextPDF>
            {(cvContent["headlines"] || []).map((headline, index) => (
              <Text key={index}>
                {headline}
                {index < (cvContent["headlines"] || []).length - 1 && (
                  <RedSlash />
                )}
              </Text>
            ))}
          </SmallTextPDF>
        </View>
        <BreakLine />
        <View
          style={{
            padding: `${verticalPadding} ${horizontalPadding}`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SmallTextPDF
            style={{
              color: perelynRed,
            }}
          >
            {t.educationAcademics}
          </SmallTextPDF>
          {(cvContent["education"] || []).map((edu, index) => (
            <View
              key={index}
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
              }}
            >
              <RedBulletPointSquare
                style={{
                  marginTop: "5px",
                }}
              />
              <SmallTextPDF>
                {edu.degree || ""} {edu.institution ? `(${edu.institution})` : ""}
              </SmallTextPDF>
            </View>
          ))}
        </View>
        <BreakLine />
        {((cvContent["certificates"] || []).length > 0 ||
          (cvContent["certificates_logos"] &&
            cvContent["certificates_logos"].length > 0)) && (
          <>
            <View
              style={{
                padding: `${verticalPadding} ${horizontalPadding}`,
                display: "flex",
                flexDirection: lessThanTwoCertificates ? "row" : "column",
                alignItems: lessThanTwoCertificates ? "center" : "flex-start",
                justifyContent: lessThanTwoCertificates
                  ? "space-between"
                  : "center",
                gap: "16px",
              }}
            >
              {(cvContent["certificates"] || []).length > 0 && (
                <View
                  style={{ maxWidth: certificateLogosPresent ? "70%" : "100%" }}
                >
                  {(cvContent["certificates"] || []).map(
                    (certificate, index) => (
                      <View
                        key={index}
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "16px",
                        }}
                      >
                        <RedBulletPointSquare
                          style={{
                            marginTop: "5px",
                          }}
                        />
                        <SmallTextPDF>
                          <SmallTextPDF
                            style={{
                              color: perelynRed,
                            }}
                          >
                            {certificate["label"] || ""}
                          </SmallTextPDF>
                          {`: ${certificate["name"] || ""}`}
                        </SmallTextPDF>
                      </View>
                    )
                  )}
                </View>
              )}
              {lessThanTwoCertificates && certificateLogosPresent ? (
                <Image
                  src={
                    (cvContent["certificates_logos"] || []).find(() => true) ||
                    ""
                  }
                  style={{
                    width: logoSize,
                  }}
                />
              ) : (
                <View
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  {(cvContent["certificates_logos"] || []).map(
                    (logo, index) => (
                      <Image
                        key={index}
                        src={logo || ""}
                        style={{
                          width: logoSize,
                        }}
                      />
                    )
                  )}
                </View>
              )}
            </View>
            <BreakLine />
          </>
        )}
        <View
          style={{
            padding: `${verticalPadding} ${horizontalPadding} 0 ${horizontalPadding}`,
          }}
        >
          <Table>
            <HeaderRow label={t.nationalityLanguages} />
            <Row>
              <SmallTextPDF
                style={{
                  width: "30%",
                }}
              >
                {t.nationality}:
              </SmallTextPDF>
              <SmallTextPDF>{cvContent["nationality"] || ""}</SmallTextPDF>
            </Row>
            <Row last>
              <SmallTextPDF
                style={{
                  width: "30%",
                }}
              >
                {t.languages}:
              </SmallTextPDF>
              <View>
                {(cvContent["languages"] || []).map((language, index) => (
                  <View
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <SmallTextPDF
                      style={{
                        width: "40%",
                      }}
                    >
                      {language.name || ""}:
                    </SmallTextPDF>
                    <SmallTextPDF>{language.level || ""}</SmallTextPDF>
                  </View>
                ))}
              </View>
            </Row>
          </Table>
        </View>
      </>
    )
  );
}
